import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';
import { getPublicKey as nobleGetPublicKey, Point, Signature, signSync, utils, } from '@noble/secp256k1';
import { Buffer, bytesToHex, hexToBigInt, intToHex, parseRecoverableSignatureVrs, privateKeyToBuffer, PRIVATE_KEY_COMPRESSED_LENGTH, signatureRsvToVrs, signatureVrsToRsv, } from '@stacks/common';
import { c32address } from 'c32check';
import { addressFromVersionHash, addressHashModeToVersion, addressToString, createMessageSignature, } from './common';
import { AddressHashMode, COMPRESSED_PUBKEY_LENGTH_BYTES, PubKeyEncoding, StacksMessageType, TransactionVersion, UNCOMPRESSED_PUBKEY_LENGTH_BYTES, } from './constants';
import { BufferArray, hash160, hashP2PKH } from './utils';
utils.hmacSha256Sync = (key, ...msgs) => {
    const h = hmac.create(sha256, key);
    msgs.forEach(msg => h.update(msg));
    return h.digest();
};
export function getAddressFromPrivateKey(privateKey, transactionVersion = TransactionVersion.Mainnet) {
    const pubKey = pubKeyfromPrivKey(privateKey);
    return getAddressFromPublicKey(pubKey.data, transactionVersion);
}
export function getAddressFromPublicKey(publicKey, transactionVersion = TransactionVersion.Mainnet) {
    publicKey = typeof publicKey === 'string' ? publicKey : publicKey.toString('hex');
    const addrVer = addressHashModeToVersion(AddressHashMode.SerializeP2PKH, transactionVersion);
    const addr = addressFromVersionHash(addrVer, hashP2PKH(Buffer.from(publicKey, 'hex')));
    const addrString = addressToString(addr);
    return addrString;
}
export function createStacksPublicKey(key) {
    return {
        type: StacksMessageType.PublicKey,
        data: Buffer.from(key, 'hex'),
    };
}
export function publicKeyFromSignatureVrs(message, messageSignature, pubKeyEncoding = PubKeyEncoding.Compressed) {
    const parsedSignature = parseRecoverableSignatureVrs(messageSignature.data);
    const signature = new Signature(hexToBigInt(parsedSignature.r), hexToBigInt(parsedSignature.s));
    const point = Point.fromSignature(message, signature, parsedSignature.recoveryId);
    const compressed = pubKeyEncoding === PubKeyEncoding.Compressed;
    return point.toHex(compressed);
}
export function publicKeyFromSignatureRsv(message, messageSignature, pubKeyEncoding = PubKeyEncoding.Compressed) {
    return publicKeyFromSignatureVrs(message, Object.assign(Object.assign({}, messageSignature), { data: signatureRsvToVrs(messageSignature.data) }), pubKeyEncoding);
}
export const publicKeyFromSignature = publicKeyFromSignatureVrs;
export function publicKeyFromBuffer(data) {
    return { type: StacksMessageType.PublicKey, data };
}
export function isCompressed(key) {
    return !key.data.toString('hex').startsWith('04');
}
export function publicKeyToString(key) {
    return key.data.toString('hex');
}
export function serializePublicKey(key) {
    const bufferArray = new BufferArray();
    bufferArray.push(key.data);
    return bufferArray.concatBuffer();
}
export function pubKeyfromPrivKey(privateKey) {
    const privKey = createStacksPrivateKey(privateKey);
    const publicKey = nobleGetPublicKey(privKey.data.slice(0, 32), privKey.compressed);
    return createStacksPublicKey(bytesToHex(publicKey));
}
export function compressPublicKey(publicKey) {
    const hex = typeof publicKey === 'string' ? publicKey : bytesToHex(publicKey);
    const compressed = Point.fromHex(hex).toHex(true);
    return createStacksPublicKey(compressed);
}
export function deserializePublicKey(bufferReader) {
    const fieldId = bufferReader.readUInt8();
    const keyLength = fieldId !== 4 ? COMPRESSED_PUBKEY_LENGTH_BYTES : UNCOMPRESSED_PUBKEY_LENGTH_BYTES;
    return publicKeyFromBuffer(Buffer.concat([Buffer.from([fieldId]), bufferReader.readBuffer(keyLength)]));
}
export function createStacksPrivateKey(key) {
    const data = privateKeyToBuffer(key);
    const compressed = data.length == PRIVATE_KEY_COMPRESSED_LENGTH;
    return { data, compressed };
}
export function makeRandomPrivKey() {
    return createStacksPrivateKey(bytesToHex(utils.randomPrivateKey()));
}
export function signWithKey(privateKey, messageHash) {
    const [rawSignature, recoveryId] = signSync(messageHash, privateKey.data.slice(0, 32), {
        canonical: true,
        recovered: true,
    });
    if (recoveryId == null) {
        throw new Error('No signature recoveryId received');
    }
    const recoveryIdHex = intToHex(recoveryId, 1);
    const recoverableSignatureString = recoveryIdHex + Signature.fromHex(rawSignature).toCompactHex();
    return createMessageSignature(recoverableSignatureString);
}
export function signMessageHashRsv({ messageHash, privateKey, }) {
    const messageSignature = signWithKey(privateKey, messageHash);
    return Object.assign(Object.assign({}, messageSignature), { data: signatureVrsToRsv(messageSignature.data) });
}
export const parseRecoverableSignature = parseRecoverableSignatureVrs;
export function getPublicKey(privateKey) {
    return pubKeyfromPrivKey(privateKey.data);
}
export function privateKeyToString(privateKey) {
    return privateKey.data.toString('hex');
}
export function publicKeyToAddress(version, publicKey) {
    return c32address(version, hash160(publicKey.data).toString('hex'));
}
//# sourceMappingURL=keys.js.map